'use client';
import React from 'react';
import styled from '@benzinga/themetron';

import { StockSymbol, UnixTimestamp } from '@benzinga/session';
import { BaseQuote, StoryCategory } from '@benzinga/advanced-news-manager';
import { Optional } from '@benzinga/utils';
import ProTicker from './Ticker/Ticker';
import { TickerPopover } from './TickerPopover';
import { ContextMenu, linkingContextOption, openWithContextOption, watchlistContextOption } from '../ContextMenu';
import { SessionContext } from '@benzinga/session-context';
import { TickerSelectedEvent, WidgetLinkingManager } from '@benzinga/widget-linking';
import { WatchlistManager } from '@benzinga/watchlist-manager';
import { ProContext } from '@benzinga/pro-tools';
import Hooks from '@benzinga/hooks';

const TICKER_CUTOFF = 9;

interface Props {
  baseQuotes?: Record<StockSymbol, Optional<BaseQuote, 'symbol'>>;
  displayedSymbolName?: string;
  isHoverShowAll?: boolean;
  isHovered: boolean;
  limit?: number;
  referenceTime?: UnixTimestamp;
  sortedSymbols: StoryCategory[];
  refHeight: number | undefined;
  onSymbolClick: (symbol: StockSymbol) => void;
}

const MultipleTickerHover: React.FC<React.PropsWithChildren<Props>> = React.memo(props => {
  const [isDialog, setIsDialog] = React.useState<boolean>(false);
  const [isContentHovered, setIsContentHovered] = React.useState(false);
  const session = React.useContext(SessionContext);
  const proContext = React.useContext(ProContext);
  const linkingManager = session.getManager(WidgetLinkingManager);
  const watchlistManager = session.getManager(WatchlistManager);

  const [widgetLinks, setWidgetLinks] = React.useState(() => linkingManager.getWidgetLinks());
  Hooks.useSubscriber(linkingManager, e => {
    switch (e.type) {
      case 'linking:link_updated': {
        setWidgetLinks(linkingManager.getWidgetLinks());
        break;
      }
    }
  });

  const handleContentMouseEnter = React.useCallback(() => {
    setIsContentHovered(true);
  }, []);

  const handleContentMouseLeave = React.useCallback(() => {
    setIsContentHovered(false);
  }, []);

  const onDialogModeChange = (isFocused: boolean) => {
    setIsDialog(isFocused);
  };

  const calculateChangeSincePublish = (storyQuote?: Optional<BaseQuote, 'symbol'>) => {
    if (!storyQuote) {
      return {};
    }

    return {
      changeSincePublish: storyQuote,
    };
  };

  const renderHover = () => {
    const numberOfSymbols = props.sortedSymbols.length;
    if (props.displayedSymbolName && (numberOfSymbols === 1 || !props.isHoverShowAll)) {
      const modules = {
        ...calculateChangeSincePublish(props.baseQuotes?.[props.displayedSymbolName as StockSymbol]),
        chart: {
          referenceTime: props.referenceTime,
        },
      };
      return (
        <div onMouseEnter={handleContentMouseEnter} onMouseLeave={handleContentMouseLeave}>
          <ProTicker
            isOnlySymbol
            key={props.displayedSymbolName}
            modules={modules}
            onDialogModeChange={onDialogModeChange}
            onSymbolClick={props.onSymbolClick}
            symbol={props.displayedSymbolName}
          />
        </div>
      );
    }

    const toDrop = props.limit ?? 0;

    const displayedSymbols = props.sortedSymbols?.slice(toDrop)?.map(ticker => (
      <ContextMenu
        key={ticker.name}
        menu={[
          linkingContextOption(widgetLinks, linkId => {
            const feed = linkingManager.getWidgetLinkFeedByID(linkId);
            feed?.pushEvent({
              symbol: ticker.name,
              type: 'ticker_selected',
            } satisfies TickerSelectedEvent);
          }),
          watchlistContextOption(ticker.name, watchlistManager),
          openWithContextOption(ticker.name, proContext.addWidgetWithSymbol),
        ]}
      >
        <div>
          <ProTicker
            key={ticker.name}
            modules={{
              ...calculateChangeSincePublish(props.baseQuotes?.[ticker.name as StockSymbol]),
              chart: {
                referenceTime: props.referenceTime,
              },
            }}
            onDialogModeChange={onDialogModeChange}
            onSymbolClick={props.onSymbolClick}
            symbol={ticker.name}
          />
        </div>
      </ContextMenu>
    ));

    return (
      <TableDiv
        $hasExtraSymbols={numberOfSymbols <= toDrop + TICKER_CUTOFF ? false : true}
        onMouseEnter={handleContentMouseEnter}
        onMouseLeave={handleContentMouseLeave}
      >
        {displayedSymbols}
      </TableDiv>
    );
  };

  return (
    <TickerPopover
      content={renderHover()}
      positions={['right', 'left']}
      refHeight={props.refHeight}
      visible={isDialog || props.isHovered || isContentHovered}
    >
      {props.children}
    </TickerPopover>
  );
});

const TableDiv = styled.div<{ $hasExtraSymbols?: boolean }>`
  background-color: ${props => props.theme.colors.backgroundActive};
  border-collapse: collapse;
  color: ${props => props.theme.colors.foregroundActive};
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  height: ${prop => (prop.$hasExtraSymbols ? '200px !important' : 'auto')};
  overflow-y: ${prop => (prop.$hasExtraSymbols ? 'scroll' : 'auto')};
`;

export default MultipleTickerHover;
